import {
  AppstoreOutlined,
  DashboardOutlined,
  DoubleLeftOutlined,
  FolderOpenOutlined,
  LogoutOutlined,
  MenuFoldOutlined,
  MenuUnfoldOutlined,
  MoneyCollectOutlined,
  MobileOutlined,
  PictureOutlined,
  CloudServerOutlined,
  SettingOutlined,
  TeamOutlined,
  UserAddOutlined,
  UserOutlined,
} from '@ant-design/icons';
import { Avatar, Badge, Button, Col, Divider, Form, Input, Layout, Menu, Modal, Row, Segmented, Spin, Tag, Typography, message, notification } from 'antd';
import { Suspense, useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { adminRequest, clearToken, cloud, getAdminBrand, getCurrentSystemId, getStaffProfile, getToken, setCurrentSystemId, staffLogin } from './api';
import { ImageUploadField, imageCropPresets } from './components/ImageUploadField';
import type { ImageUploadFieldHandle } from './components/ImageUploadField';
import { lazyPage } from './lazy-page';
import { resolveSystemSelection } from './system-selection';
import type { AdminBrand, CreatorApplication, StaffProfile } from './types';

const DashboardPage = lazyPage(() => import('./pages/DashboardPage'), 'DashboardPage');
const WallpapersPage = lazyPage(() => import('./pages/WallpapersPage'), 'WallpapersPage');
const CategoriesPage = lazyPage(() => import('./pages/CatalogPages'), 'CategoriesPage');
const AlbumsPage = lazyPage(() => import('./pages/CatalogPages'), 'AlbumsPage');
const BannersPage = lazyPage(() => import('./pages/CatalogPages'), 'BannersPage');
const UsersPage = lazyPage(() => import('./pages/UsersPage'), 'UsersPage');
const ContentPage = lazyPage(() => import('./pages/ContentPage'), 'ContentPage');
const StoragePage = lazyPage(() => import('./pages/StoragePage'), 'StoragePage');
const DesignersPage = lazyPage(() => import('./pages/DesignersPage'), 'DesignersPage');
const TeamMembersPage = lazyPage(() => import('./pages/TeamMembersPage'), 'TeamMembersPage');
const MembershipRevenuePage = lazyPage(() => import('./pages/MembershipRevenuePage'), 'MembershipRevenuePage');
const MiniAppsPage = lazyPage(() => import('./pages/MiniAppsPage'), 'MiniAppsPage');

const { Header, Sider, Content } = Layout;
type SidebarMode = 'expanded' | 'icons' | 'hidden';
const defaultAdminBrand: AdminBrand = { logoUrl: '', title: '潮图后台', subtitle: 'CONTENT CONSOLE' };
const BrandLogo = ({ brand }: { brand: AdminBrand }) => brand.logoUrl
  ? <img src={brand.logoUrl} alt="" />
  : <span>潮</span>;
const getInitialSidebarMode = (): SidebarMode => {
  const saved = localStorage.getItem('chaotu_sidebar_mode');
  return saved === 'icons' || saved === 'hidden' ? saved : 'expanded';
};
const applicationsLabel = (count: number, label: string) => <span className="menu-label-with-badge"><span>{label}</span>{count > 0 ? <Badge count={count} overflowCount={99} /> : null}</span>;
const rootMenuItems = (pendingApplications: number) => [
  { key: 'dashboard', icon: <DashboardOutlined />, label: '总览' },
  { key: 'wallpapers', icon: <PictureOutlined />, label: '壁纸管理' },
  { key: 'categories', icon: <AppstoreOutlined />, label: '分类管理' },
  { key: 'albums', icon: <FolderOpenOutlined />, label: '专辑管理' },
  { key: 'banners', icon: <PictureOutlined />, label: '轮播图管理' },
  { key: 'users', icon: <TeamOutlined />, label: '用户管理' },
  { key: 'designers', icon: <UserAddOutlined />, label: applicationsLabel(pendingApplications, '账号与权限') },
  { key: 'content', icon: <SettingOutlined />, label: '运营配置' },
  { key: 'membership-revenue', icon: <MoneyCollectOutlined />, label: '会员与收益' },
  { key: 'miniapps', icon: <MobileOutlined />, label: '小程序与流量' },
  { key: 'storage', icon: <CloudServerOutlined />, label: '服务配置' },
];
function designerMenuItems(profile: StaffProfile) {
  const canReview = profile.permissions.includes('wallpaper:review') || profile.permissions.includes('wallpaper:team-review');
  return [
    { key: 'dashboard', icon: <DashboardOutlined />, label: '总览' },
    { key: 'wallpapers', icon: <PictureOutlined />, label: canReview ? '壁纸审核' : '上传壁纸' },
    ...(profile.isTeamLeader ? [{ key: 'team-members', icon: <TeamOutlined />, label: '团队成员' }] : []),
  ];
}

function subadminMenuItems(profile: StaffProfile, pendingApplications = 0) {
  const permissions = new Set(profile.permissions);
  return [
    ...(permissions.has('dashboard:view') ? [{ key: 'dashboard', icon: <DashboardOutlined />, label: '总览' }] : []),
    ...(permissions.has('wallpaper:review') ? [{ key: 'wallpapers', icon: <PictureOutlined />, label: '壁纸审核' }] : []),
    ...(permissions.has('category:manage') ? [{ key: 'categories', icon: <AppstoreOutlined />, label: '分类管理' }] : []),
    ...(permissions.has('album:manage') ? [{ key: 'albums', icon: <FolderOpenOutlined />, label: '专辑管理' }] : []),
    ...(permissions.has('banner:manage') ? [{ key: 'banners', icon: <PictureOutlined />, label: '轮播图管理' }] : []),
    ...(permissions.has('user:manage') ? [{ key: 'users', icon: <TeamOutlined />, label: '用户管理' }] : []),
    ...(permissions.has('designer:manage') ? [{ key: 'designers', icon: <UserAddOutlined />, label: applicationsLabel(pendingApplications, '创作者与申请') }] : []),
    ...(permissions.has('content:manage') ? [{ key: 'content', icon: <SettingOutlined />, label: '运营配置' }] : []),
    ...(permissions.has('membership:manage') ? [{ key: 'membership-revenue', icon: <MoneyCollectOutlined />, label: '会员与收益' }] : []),
    ...(permissions.has('miniapp:manage') ? [{ key: 'miniapps', icon: <MobileOutlined />, label: '小程序与流量' }] : []),
    ...(permissions.has('storage:manage') ? [{ key: 'storage', icon: <CloudServerOutlined />, label: '服务配置' }] : []),
  ];
}

function LoginPage({ onSuccess, systemId, brand }: { onSuccess: (profile: StaffProfile) => void; systemId: string; brand: AdminBrand }) {
  const [loading, setLoading] = useState(false);
  const [mode, setMode] = useState<'admin' | 'designer'>('designer');
  const submit = async (values: { username?: string; password?: string; adminUsername?: string; adminPassword?: string }) => {
    setLoading(true);
    try {
      const profile = await staffLogin(mode === 'admin'
        ? { loginType: 'admin', username: String(values.adminUsername || 'admin'), password: String(values.adminPassword || '') }
        : { loginType: 'designer', username: String(values.username || ''), password: String(values.password || '') }, systemId);
      message.success('登录成功');
      onSuccess(profile);
    }
    catch (error) { message.error((error as Error).message); }
    finally { setLoading(false); }
  };
  return (
    <div className="login-shell">
      <div className="login-orb orb-one" /><div className="login-orb orb-two" />
      <section className="login-story">
        <div className="brand-mark large"><BrandLogo brand={brand} /></div>
        <Typography.Title>让每一张好图，<br />准时抵达每一块屏幕。</Typography.Title>
        <Typography.Paragraph>微信、抖音、快手统一内容中枢</Typography.Paragraph>
        <div className="login-chips"><Tag>三端同步</Tag><Tag>实时发布</Tag><Tag>安全存储</Tag></div>
      </section>
      <section className="login-panel">
        <div className="login-card">
          <div className="mobile-logo"><div className="brand-mark"><BrandLogo brand={brand} /></div><strong>{brand.title}</strong></div>
          <Typography.Title level={2}>欢迎回来</Typography.Title>
          <Typography.Paragraph type="secondary">管理员审核内容，创作者提交壁纸</Typography.Paragraph>
          {systemId !== 'official' && brand.systemName ? <Tag color="cyan" style={{ marginBottom: 16 }}>当前子系统：{brand.systemName}</Tag> : null}
          <Segmented block value={mode} onChange={value => setMode(value as 'admin' | 'designer')} options={[{ label: '创作者', value: 'designer' }, { label: '管理员', value: 'admin' }]} />
          <Form layout="vertical" onFinish={submit} requiredMark={false} size="large">
            {mode === 'admin' ? <>
              <Form.Item name="adminUsername" label="管理员账号" initialValue="admin" rules={[{ required: true, message: '请输入管理员账号' }]}><Input autoFocus autoComplete="username" placeholder="主管理员账号为 admin" /></Form.Item>
              <Form.Item name="adminPassword" label="密码 / 管理密钥" rules={[{ required: true, message: '请输入密码或管理密钥' }]}><Input.Password autoComplete="current-password" placeholder="输入副管理员密码或主管理员密钥" /></Form.Item>
            </> : <>
              <Form.Item name="username" label="创作者账号" rules={[{ required: true, message: '请输入账号' }]}><Input autoFocus autoComplete="username" placeholder="输入创作者账号" /></Form.Item>
              <Form.Item name="password" label="登录密码" rules={[{ required: true, message: '请输入密码' }]}><Input.Password autoComplete="current-password" placeholder="输入登录密码" /></Form.Item>
            </>}
            <Button type="primary" htmlType="submit" block loading={loading}>进入管理后台</Button>
          </Form>
          <div className="login-security">受 HTTPS 加密保护 · 连续输错 5 次将锁定 15 分钟</div>
          <Button type="link" block href="/control">进入平台总管理层</Button>
        </div>
      </section>
    </div>
  );
}

export default function App() {
  const [systemId] = useState(() => {
    const selection = resolveSystemSelection(window.location.search, getCurrentSystemId());
    if (selection.searchChanged) {
      window.history.replaceState(null, '', `${window.location.pathname}${selection.sanitizedSearch}${window.location.hash}`);
    }
    if (selection.storedSystemInvalid) setCurrentSystemId('official');
    return selection.systemId;
  });
  const [adminBrand, setAdminBrand] = useState<AdminBrand>(defaultAdminBrand);
  const [authenticated, setAuthenticated] = useState(Boolean(getToken()));
  const [checking, setChecking] = useState(Boolean(getToken()));
  const [staff, setStaff] = useState<StaffProfile | null>(null);
  const [sidebarMode, setSidebarMode] = useState<SidebarMode>(getInitialSidebarMode);
  const [page, setPage] = useState(() => localStorage.getItem('chaotu_admin_page') || 'dashboard');
  const [profileOpen, setProfileOpen] = useState(false);
  const [profileSaving, setProfileSaving] = useState(false);
  const [pendingApplicationCount, setPendingApplicationCount] = useState(0);
  const [accountTab, setAccountTab] = useState<'designer' | 'applications'>('designer');
  const [profileForm] = Form.useForm<{ displayName: string; avatarUrl?: string; contactName?: string; phone?: string; wechat?: string; adminBrandLogoUrl?: string; adminBrandTitle?: string; adminBrandSubtitle?: string }>();
  const profileAvatarRef = useRef<ImageUploadFieldHandle>(null);
  const brandLogoRef = useRef<ImageUploadFieldHandle>(null);
  const applicationNoticeStaff = useRef('');
  const [notificationApi, notificationContextHolder] = notification.useNotification();

  const canManageApplications = staff?.role === 'admin' || (staff?.role === 'subadmin' && staff.permissions.includes('designer:manage'));
  const openApplications = useCallback(() => {
    setAccountTab('applications');
    setPage('designers');
    localStorage.setItem('chaotu_admin_page', 'designers');
    notificationApi.destroy('pending-creator-applications');
  }, [notificationApi]);
  const refreshPendingApplications = useCallback(async (showLoginNotice = false) => {
    if (!staff || !canManageApplications) {
      setPendingApplicationCount(0);
      return;
    }
    try {
      const result = await cloud<CreatorApplication[]>('creatorApplications', { action: 'list', status: 'pending', page: 1, pageSize: 1 });
      const count = result.total ?? result.data?.length ?? 0;
      setPendingApplicationCount(count);
      if (showLoginNotice && count > 0 && applicationNoticeStaff.current !== staff.id) {
        applicationNoticeStaff.current = staff.id;
        notificationApi.warning({
          key: 'pending-creator-applications',
          message: `有 ${count} 条待处理的入驻申请`,
          description: '新的创作者正在等待联系，请及时进入“账号与权限”处理。',
          duration: 0,
          placement: 'topRight',
          btn: <Button type="primary" size="small" onClick={openApplications}>立即处理</Button>,
        });
      }
    } catch (error) {
      console.warn('读取待处理入驻申请失败', error);
    }
  }, [canManageApplications, notificationApi, openApplications, staff]);

  useEffect(() => {
    getAdminBrand(systemId)
      .then(brand => {
        setAdminBrand(brand);
        document.title = brand.title;
      })
      .catch(() => setAdminBrand(defaultAdminBrand));
  }, [systemId]);

  useEffect(() => {
    if (!getToken()) return;
    getStaffProfile()
      .then(profile => { setStaff(profile); setAuthenticated(true); })
      .catch(() => { clearToken(); setAuthenticated(false); setStaff(null); })
      .finally(() => setChecking(false));
  }, []);

  useEffect(() => {
    void refreshPendingApplications(true);
  }, [refreshPendingApplications]);

  useEffect(() => {
    if (staff?.role === 'designer') {
      const allowed = designerMenuItems(staff);
      if (!allowed.some(item => item.key === page)) {
        setPage('dashboard');
        localStorage.setItem('chaotu_admin_page', 'dashboard');
      }
    }
    if (staff?.role === 'subadmin') {
      const allowed = subadminMenuItems(staff, pendingApplicationCount);
      if (!allowed.some(item => item.key === page) && allowed[0]) {
        setPage(allowed[0].key);
        localStorage.setItem('chaotu_admin_page', allowed[0].key);
      } else if (!allowed.length && page !== 'no-access') {
        setPage('no-access');
        localStorage.setItem('chaotu_admin_page', 'no-access');
      }
    }
  }, [staff, page, pendingApplicationCount]);

  const currentPage = useMemo(() => ({
    dashboard: <DashboardPage staff={staff} />,
    wallpapers: <WallpapersPage staff={staff} />,
    categories: <CategoriesPage />,
    albums: <AlbumsPage />,
    banners: <BannersPage />,
    users: <UsersPage />,
    content: <ContentPage />,
    'membership-revenue': <MembershipRevenuePage />,
    miniapps: <MiniAppsPage staff={staff} />,
    storage: <StoragePage />,
    designers: <DesignersPage staff={staff} initialTab={accountTab} onApplicationsChanged={() => void refreshPendingApplications()} onRootKeyChanged={() => { clearToken(); setAuthenticated(false); setStaff(null); }} />,
    'team-members': <TeamMembersPage />,
    'no-access': <div className="empty-access"><Typography.Title level={3}>暂未分配权限</Typography.Title><Typography.Paragraph type="secondary">请联系主管理员为该副管理员分配至少一项后台权限。</Typography.Paragraph></div>,
  }[page] || <DashboardPage staff={staff} />), [accountTab, page, refreshPendingApplications, staff]);

  const menuItems = staff?.role === 'designer'
    ? designerMenuItems(staff)
    : staff?.role === 'subadmin'
      ? subadminMenuItems(staff, pendingApplicationCount)
      : rootMenuItems(pendingApplicationCount);

  const navigate = (key: string) => { setPage(key); localStorage.setItem('chaotu_admin_page', key); };
  const changeSidebarMode = (mode: SidebarMode) => {
    setSidebarMode(mode);
    localStorage.setItem('chaotu_sidebar_mode', mode);
  };
  const cycleSidebarMode = () => {
    changeSidebarMode(sidebarMode === 'expanded' ? 'icons' : sidebarMode === 'icons' ? 'hidden' : 'expanded');
  };
  const sidebarButtonTitle = sidebarMode === 'expanded' ? '收起为图标菜单' : sidebarMode === 'icons' ? '完全隐藏菜单' : '展开完整菜单';
  const sidebarButtonIcon = sidebarMode === 'expanded' ? <MenuFoldOutlined /> : sidebarMode === 'icons' ? <DoubleLeftOutlined /> : <MenuUnfoldOutlined />;
  const logout = () => Modal.confirm({
    title: '退出管理后台？', content: '当前浏览器的登录令牌将被清除。', okText: '退出', cancelText: '取消',
    onOk: () => { clearToken(); applicationNoticeStaff.current = ''; setPendingApplicationCount(0); setAuthenticated(false); setStaff(null); },
  });
  const openProfile = () => {
    profileForm.setFieldsValue({
      displayName: staff?.displayName || '',
      avatarUrl: staff?.avatarUrl || '',
      contactName: staff?.contactName || '',
      phone: staff?.phone || '',
      wechat: staff?.wechat || '',
      adminBrandLogoUrl: adminBrand.logoUrl,
      adminBrandTitle: adminBrand.title,
      adminBrandSubtitle: adminBrand.subtitle,
    });
    setProfileOpen(true);
  };
  const saveProfile = async () => {
    const values = await profileForm.validateFields();
    setProfileSaving(true);
    try {
      const result = await adminRequest<StaffProfile>('/api/admin/profile', { method: 'PUT', data: values });
      if (staff?.role === 'admin') {
        const brandResult = await adminRequest<AdminBrand>('/api/admin/brand', {
          method: 'PUT',
          data: {
            logoUrl: values.adminBrandLogoUrl || '',
            title: values.adminBrandTitle || '',
            subtitle: values.adminBrandSubtitle || '',
          },
        });
        brandLogoRef.current?.commit();
        setAdminBrand(brandResult.data);
        document.title = brandResult.data.title;
      }
      profileAvatarRef.current?.commit();
      setStaff(result.data);
      setProfileOpen(false);
      message.success('个人资料已更新');
    } catch (error) {
      message.error((error as Error).message);
    } finally {
      setProfileSaving(false);
    }
  };

  if (checking) return <div className="full-spin"><Spin size="large" tip="正在验证登录状态" /></div>;
  if (!authenticated) return <LoginPage systemId={systemId} brand={adminBrand} onSuccess={profile => { applicationNoticeStaff.current = ''; setStaff(profile); setAuthenticated(true); if (profile.role === 'designer') navigate('dashboard'); }} />;

  return (
    <>
    {notificationContextHolder}
    <Layout className="admin-layout">
      <Sider className={`admin-sider${sidebarMode === 'hidden' ? ' is-hidden' : ''}`} width={248} collapsedWidth={sidebarMode === 'hidden' ? 0 : 76} collapsed={sidebarMode !== 'expanded'} trigger={null} breakpoint="lg" onBreakpoint={broken => { if (broken) changeSidebarMode('hidden'); }}>
        <div className={`sidebar-brand ${sidebarMode !== 'expanded' ? 'collapsed' : ''}`}><div className="brand-mark"><BrandLogo brand={adminBrand} /></div>{sidebarMode === 'expanded' ? <div><strong>{adminBrand.title}</strong>{adminBrand.subtitle ? <small>{adminBrand.subtitle}</small> : null}</div> : null}</div>
        <Menu mode="inline" theme="dark" selectedKeys={[page]} items={menuItems} onClick={({ key }) => navigate(key)} />
        <div className="sidebar-footer"><Button type="text" icon={<LogoutOutlined />} onClick={logout}>{sidebarMode === 'expanded' ? '退出登录' : null}</Button></div>
      </Sider>
      <Layout>
        <Header className="admin-header">
          <Button type="text" className="collapse-button" title={sidebarButtonTitle} aria-label={sidebarButtonTitle} icon={sidebarButtonIcon} onClick={cycleSidebarMode} />
          {pendingApplicationCount > 0 ? <Button className="pending-application-alert" danger onClick={openApplications}><Badge count={pendingApplicationCount} overflowCount={99} /> 待处理入驻申请</Button> : null}
          <button type="button" className="profile-trigger" onClick={openProfile} title={staff?.role === 'admin' ? '个人资料与后台品牌' : '设置个人头像'}>
            <Avatar className="profile-avatar" shape="square" size={36} src={staff?.avatarUrl} icon={<UserOutlined />} />
            <span className="profile-identity">
              <span>{staff?.displayName || '服务器在线'}</span>
              {staff?.systemId && staff.systemId !== 'official' ? <small>子系统：{staff.systemId}</small> : null}
              {staff?.role === 'designer' ? <small className="creator-code-badge">口令：<strong>{staff.creatorCode || '—'}</strong></small> : null}
            </span>
            <Tag color={staff?.role === 'designer' ? 'purple' : staff?.role === 'subadmin' ? 'blue' : 'cyan'}>{staff?.role === 'designer' ? '创作者' : staff?.role === 'subadmin' ? '副管理员' : '主管理员'}</Tag>
          </button>
        </Header>
        <Content className="admin-content"><div className="content-inner"><Suspense fallback={<div className="page-loading"><Spin size="large" /></div>}>{currentPage}</Suspense></div></Content>
      </Layout>
    </Layout>
    <Modal width={staff?.role === 'subadmin' ? 520 : 680} open={profileOpen} title={staff?.role === 'admin' ? '个人资料与后台品牌' : '个人资料'} onCancel={() => { void profileAvatarRef.current?.discard().catch(() => {}); void brandLogoRef.current?.discard().catch(() => {}); setProfileOpen(false); }} onOk={saveProfile} confirmLoading={profileSaving} okText="保存" cancelText="取消">
      <Form form={profileForm} layout="vertical" requiredMark={false}>
        <Form.Item name="avatarUrl" label="个人头像"><ImageUploadField ref={profileAvatarRef} label="上传头像" assetType="avatars" /></Form.Item>
        {staff?.role === 'designer' ? <>
          <Row gutter={12}>
            <Col xs={24} sm={12}><Form.Item name="displayName" label="显示名称" rules={[{ required: true, message: '请输入显示名称' }]}><Input maxLength={40} /></Form.Item></Col>
            <Col xs={24} sm={12}><Form.Item name="contactName" label="联系人"><Input maxLength={40} placeholder="真实姓名或联系人称呼" /></Form.Item></Col>
          </Row>
          <Row gutter={12}>
            <Col xs={24} sm={12}><Form.Item name="phone" label="手机号"><Input maxLength={40} placeholder="手机号或联系电话" /></Form.Item></Col>
            <Col xs={24} sm={12}><Form.Item name="wechat" label="微信 / QQ"><Input maxLength={80} placeholder="微信号、QQ 或其他账号" /></Form.Item></Col>
          </Row>
        </> : <Form.Item name="displayName" label="显示名称" rules={[{ required: true, message: '请输入显示名称' }]}><Input maxLength={40} /></Form.Item>}
        {staff?.role === 'admin' ? <>
          <Divider titlePlacement="start">后台品牌设置</Divider>
          <Form.Item name="adminBrandLogoUrl" label="后台图标" extra="上传前裁剪为 1:1，输出 96×96 WebP"><ImageUploadField ref={brandLogoRef} label="上传图标" assetType="ui" square crop={imageCropPresets.icon} /></Form.Item>
          <Row gutter={12}>
            <Col xs={24} sm={12}><Form.Item name="adminBrandTitle" label="后台名称" rules={[{ required: true, message: '请输入后台名称' }]}><Input maxLength={40} placeholder="潮图后台" /></Form.Item></Col>
            <Col xs={24} sm={12}><Form.Item name="adminBrandSubtitle" label="英文副标题（可留空）"><Input maxLength={60} placeholder="CONTENT CONSOLE" /></Form.Item></Col>
          </Row>
        </> : null}
      </Form>
    </Modal>
    </>
  );
}
